import {
  computed,
  inject,
  Injectable,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import {
  CONFIGURATION_SETTING_KEYS,
  EPaymentProviders,
  ETransactionSource,
  IBuyResponse,
  IConfiguredDirectTicket,
  IAdditionalEntryValue,
  IDirectTicket,
  IPurchase,
  ITicket,
  NamedTariffProductDefinition,
  PaymentMethod,
  TariffProductDefinition,
  TariffProductGroup,
  TariffSelectionAttributeValue,
  ITariffAdditionalEntry,
  UserDto,
} from '@web/dtos';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { ITicketPersonalDataForm } from '../../pages/shop/direct-ticket-configuration-page/direct-ticket-personal-data-page/direct-ticket-personal-data-page.component';
import { ITicketOwnerDataForm } from '../../pages/shop/direct-ticket-configuration-page/direct-ticket-owner-data-page/direct-ticket-owner-data-page.component';
import { ConfigurationStore } from '../api/configuration/configuration-store.service';
import { ActiveDirectTicketStore } from '../api/direct-ticket/active/active-direct-ticket-store.service';
import {
  ITicketDefinitionForm,
  ShoppingCartFormService,
} from './shopping-cart-form.service';
import { Observable } from 'rxjs';
import { IApiResponse } from '@web/shared-types';
import { formatDateOnly } from '@web/shared-functions/date';

const MIN_AMOUNT = 1;
const MAX_AMOUNT = 10;

export interface IProductInCart extends NamedTariffProductDefinition {
  tariffProductGroup: TariffProductGroup;
  ticket: ITicket | undefined;
  productForm: FormGroup<ITicketDefinitionForm>;
  owner?: FormArray<FormGroup<ITicketOwnerDataForm>>;
}

interface IOrderOptionsForm {
  shouldSaveFellowTravelers: FormControl<boolean>;
}

@Injectable({
  providedIn: 'root',
})
export class ShoppingCartService {
  public readonly continueEditingExistingTicket: WritableSignal<boolean> =
    signal(false);
  public readonly minAmount: number = MIN_AMOUNT;
  public readonly maxAmount: number = MAX_AMOUNT;
  protected readonly configurationStore: ConfigurationStore =
    inject(ConfigurationStore);
  protected readonly directTicketStore: ActiveDirectTicketStore = inject(
    ActiveDirectTicketStore
  );
  public readonly tickets: Signal<IDirectTicket[]> =
    this.directTicketStore.tickets;
  public readonly ticket: Signal<IDirectTicket | undefined> =
    this.directTicketStore.ticketConfiguration;
  public readonly isLoading: Signal<boolean> = computed(
    () =>
      this.directTicketStore.isLoading() || this.configurationStore.isLoading()
  );
  public readonly directTicketConfiguration: Signal<IDirectTicket | undefined> =
    computed(() => {
      const ticketConfig: IDirectTicket | undefined =
        this.directTicketStore.ticketConfiguration();
      return ticketConfig ?? undefined;
    });
  public isLoadingTicketConfiguration: Signal<boolean> =
    this.directTicketStore.isLoading;
  protected readonly shoppingCartFormService: ShoppingCartFormService = inject(
    ShoppingCartFormService
  );
  public readonly personalDataForm: FormGroup<ITicketPersonalDataForm> =
    this.shoppingCartFormService.createPersonalDataForm();
  protected readonly formBuilder: FormBuilder = inject(FormBuilder);
  public readonly productForms: FormArray<FormGroup<ITicketDefinitionForm>> =
    this.formBuilder.array<FormGroup<ITicketDefinitionForm>>([]);
  public readonly ticketOwnerDataForms: FormArray<
    FormArray<FormGroup<ITicketOwnerDataForm>>
  > = this.formBuilder.nonNullable.array<
    FormArray<FormGroup<ITicketOwnerDataForm>>
  >([]);
  public readonly orderOptionsForm: FormGroup<IOrderOptionsForm> =
    this.formBuilder.group<IOrderOptionsForm>({
      shouldSaveFellowTravelers:
        this.formBuilder.nonNullable.control<boolean>(false),
    });
  public readonly productsInCart: WritableSignal<IProductInCart[]> = signal([]);
  public readonly ticketsInCart: Signal<IProductInCart[]> = computed(() =>
    this.productsInCart()
  );
  public get totalNumberOfTicketsInCart(): number {
    return this.productForms.controls.reduce(
      (acc: number, curr: FormGroup<ITicketDefinitionForm>) =>
        acc + curr.controls.amount.value,
      0
    );
  }
  public get isCartTooFull(): boolean {
    return (
      this.totalNumberOfTicketsInCart >
      this.configurationStore.getNumberConfiguration(
        CONFIGURATION_SETTING_KEYS.MaxShoppingCartSize
      )
    );
  }
  public readonly isTicketOwnerDataRequired: Signal<boolean> = computed(() => {
    const isAlternatePassengerDataActive: boolean =
      this.configurationStore.getBooleanConfiguration(
        CONFIGURATION_SETTING_KEYS.IsDirectSaleAlternatePassengerActive
      );
    return (
      isAlternatePassengerDataActive ||
      this.productsInCart().some(
        product =>
          !!product.productForm.controls.product.value?.maxNumberOfPeople
      )
    );
  });
  public currentProductDefinition: Signal<TariffProductDefinition | undefined> =
    computed(() => {
      return this.productsInCart()[0]?.productForm.value.product;
    });

  public addToCart(product: IProductInCart): void {
    this.productsInCart.set([product].concat(this.productsInCart()));
  }

  public findProductByAmountOfPeople(
    amountOfPeople: number,
    ticketName?: string
  ): IProductInCart | undefined {
    return this.ticketsInCart().find(
      (item: IProductInCart): boolean =>
        item.productForm.controls.amountOfPeople.value === amountOfPeople &&
        (ticketName === undefined || item.name === ticketName)
    );
  }

  public findProductByProductForm(
    productForm: FormGroup<ITicketDefinitionForm>
  ): IProductInCart | undefined {
    return this.ticketsInCart().find(
      (item: IProductInCart): boolean => item.productForm === productForm
    );
  }

  public selectProductInCart(product: IProductInCart): boolean {
    const productIndex: number = this.productsInCart().indexOf(product);
    const productFormIndex: number = this.productForms.controls.indexOf(
      product.productForm
    );

    if (productIndex < 0 || productFormIndex < 0) {
      return false;
    }

    if (productFormIndex > 0) {
      const productForm: FormGroup<ITicketDefinitionForm> =
        this.productForms.at(productFormIndex);
      this.productForms.removeAt(productFormIndex, { emitEvent: false });
      this.productForms.insert(0, productForm, { emitEvent: false });
    }

    if (product.owner) {
      const ownerIndex: number = this.ticketOwnerDataForms.controls.indexOf(
        product.owner
      );

      if (ownerIndex > 0) {
        const ownerForm: FormArray<FormGroup<ITicketOwnerDataForm>> =
          this.ticketOwnerDataForms.at(ownerIndex);
        this.ticketOwnerDataForms.removeAt(ownerIndex, { emitEvent: false });
        this.ticketOwnerDataForms.insert(0, ownerForm, { emitEvent: false });
      }
    }

    if (productIndex > 0) {
      const products: IProductInCart[] = [...this.productsInCart()];
      products.splice(productIndex, 1);
      this.productsInCart.set([product, ...products]);
    }

    this.productForms.updateValueAndValidity();
    this.ticketOwnerDataForms.updateValueAndValidity();

    return true;
  }

  /**
   * Creates and adds one independent cart item based on an existing product.
   *
   * The new item represents exactly one ticket and owns separate product and
   * owner forms, so later changes do not affect the source item.
   */
  public addProductAsSeparateCartItem(
    source: IProductInCart,
    amountOfPeople: number
  ): IProductInCart {
    const ticketConfiguration: FormGroup<ITicketDefinitionForm>['value'] = {
      ...source.productForm.getRawValue(),
      amount: 1,
      amountOfPeople,
    };
    const productForm: FormGroup<ITicketDefinitionForm> =
      this.shoppingCartFormService.createProductForm(
        source.tariffProductGroup.selectionAttributes.filter(
          attribute => attribute.name.toLowerCase() !== 'name'
        ),
        source.tariffProductGroup.selectionAttributes.filter(
          attribute => attribute.name.toLowerCase() === 'name'
        ),
        ticketConfiguration
      );
    const owner: FormArray<FormGroup<ITicketOwnerDataForm>> =
      this.shoppingCartFormService.createTicketOwnerDataForms();

    this.shoppingCartFormService.adjustNumberOfTicketOwnerForms(owner, 1);
    this.shoppingCartFormService.adjustNumberOfCompanionForms(
      owner,
      amountOfPeople
    );

    const product: IProductInCart = {
      ...source,
      productForm,
      owner,
    };

    this.productForms.insert(0, productForm);
    this.ticketOwnerDataForms.insert(0, owner);
    this.productsInCart.set([product, ...this.productsInCart()]);

    return product;
  }

  /**
   * Moves one ticket from its current people group to another one.
   * Existing groups are merged; otherwise a separate cart item is created.
   */
  public moveOneTicketToAmountOfPeople(
    source: IProductInCart,
    amountOfPeople: number
  ): IProductInCart | undefined {
    const sourceIndex: number = this.productsInCart().indexOf(source);

    if (sourceIndex < 0) {
      return undefined;
    }

    const sourceAmountControl = source.productForm.controls.amount;
    const matchingProduct: IProductInCart | undefined =
      this.findProductByAmountOfPeople(amountOfPeople, source.name);

    if (sourceAmountControl.value === 1) {
      if (!matchingProduct) {
        source.productForm.controls.amountOfPeople.setValue(amountOfPeople);
        this.adjustNumberOfCompanionForms(sourceIndex, amountOfPeople);
        source.productForm.updateValueAndValidity();
        return source;
      }

      this.removeTicket(sourceIndex);

      if (!this.selectProductInCart(matchingProduct)) {
        return undefined;
      }

      this.updateProductAmount(
        matchingProduct,
        matchingProduct.productForm.controls.amount.value + 1
      );
      return matchingProduct;
    }

    this.updateProductAmount(source, sourceAmountControl.value - 1);

    if (matchingProduct) {
      if (!this.selectProductInCart(matchingProduct)) {
        return undefined;
      }

      this.updateProductAmount(
        matchingProduct,
        matchingProduct.productForm.controls.amount.value + 1
      );
      return matchingProduct;
    }

    return this.addProductAsSeparateCartItem(source, amountOfPeople);
  }

  public loadTicketConfiguration(ticketName: string): void {
    this.directTicketStore.loadTicketConfiguration(ticketName);
  }

  public setCurrentProductDefinition(
    product: TariffProductDefinition | undefined,
    selectionAttributes: TariffSelectionAttributeValue[],
    productSortOptions: TariffSelectionAttributeValue[]
  ): void {
    this.setCurrentProductFormValuesAndValidators(
      product,
      selectionAttributes,
      productSortOptions,
      this.productForms.controls[0].value.travelDate,
      this.productForms.controls[0].value.amount
    );
  }

  public adjustNumberOfTicketOwnerForms(index: number, amount: number): void {
    if (this.ticketOwnerDataForms.controls[index]) {
      this.shoppingCartFormService.adjustNumberOfTicketOwnerForms(
        this.ticketOwnerDataForms.controls[index],
        amount
      );
    } else {
      this.shoppingCartFormService.addTicketOwnerDataForm(
        this.ticketOwnerDataForms,
        amount
      );
    }

    const amountOfPeople: number =
      this.productForms.controls[index]?.controls.amountOfPeople.value ?? 1;
    this.adjustNumberOfCompanionForms(index, amountOfPeople);
  }

  public adjustNumberOfCompanionForms(
    index: number,
    amountOfPeople: number
  ): void {
    const ticketOwnerDataForm = this.ticketOwnerDataForms.controls[index];

    if (ticketOwnerDataForm) {
      this.shoppingCartFormService.adjustNumberOfCompanionForms(
        ticketOwnerDataForm,
        amountOfPeople
      );
    }
  }

  public prefillPersonalData(userData: UserDto): void {
    this.shoppingCartFormService.prefillPersonalData(
      this.personalDataForm,
      userData
    );
  }

  public emptyShoppingCart(): void {
    this.productForms.clear();
    this.productsInCart.set([]);
  }

  public removeTicket(index: number): void {
    this.ticketOwnerDataForms.removeAt(index);
    this.productForms.removeAt(index);
    this.productsInCart.set(
      this.productsInCart().filter((p, i) => i !== index)
    );
    this.ticketOwnerDataForms.updateValueAndValidity();
    this.productForms.updateValueAndValidity();
  }

  public get products(): IConfiguredDirectTicket[] {
    return this.ticketsInCart().map(
      (product: IProductInCart): IConfiguredDirectTicket => {
        const personalData = this.personalDataForm.getRawValue();
        const { productForm, owner, ...configuredProduct } = product;
        const { additionalEntries, ...productOptions } =
          productForm.getRawValue();
        const additionalEntryDefinitions: ITariffAdditionalEntry[] =
          productForm.controls.product.value?.sort.additionalEntries ?? [];

        return {
          ...configuredProduct,
          product: productOptions,
          additionalEntries: additionalEntries.map(entry => ({
            ...entry,
            value: this.serializeAdditionalEntryValue(
              entry,
              additionalEntryDefinitions
            ),
          })),
          owners: owner?.getRawValue().map(ownerData => {
            const { fellowTravelerId, ...ticketOwnerData } = ownerData;
            const companions = ticketOwnerData.companions?.map(
              companionData => {
                const {
                  fellowTravelerId: companionFellowTravelerId,
                  ...companion
                } = companionData;
                return {
                  ...companion,
                  id: companionFellowTravelerId,
                };
              }
            );
            const sanitizedTicketOwnerData = {
              ...ticketOwnerData,
              id: fellowTravelerId,
              dateOfBirth: ticketOwnerData.dateOfBirth
                ? formatDateOnly(ticketOwnerData.dateOfBirth)
                : undefined,
              companions,
            };
            return sanitizedTicketOwnerData.isForUser
              ? {
                  ...sanitizedTicketOwnerData,
                  salutation: personalData.salutation,
                  firstName: personalData.firstName,
                  lastName: personalData.lastName,
                  dateOfBirth: personalData.dateOfBirth
                    ? formatDateOnly(personalData.dateOfBirth)
                    : undefined,
                }
              : sanitizedTicketOwnerData;
          }),
          personalData,
        };
      }
    );
  }

  private serializeAdditionalEntryValue(
    entry: IAdditionalEntryValue,
    definitions: ITariffAdditionalEntry[]
  ): IAdditionalEntryValue['value'] {
    if (!(entry.value instanceof Date)) {
      return entry.value;
    }

    const definition: ITariffAdditionalEntry | undefined = definitions.find(
      item =>
        item.externalReference === entry.externalReference &&
        item.name === entry.name
    );
    const dataType: string =
      definition?.additionalEntryDataType || definition?.dataType || '';

    if (dataType === 'DatumUhrzeit') {
      const hours: string = String(entry.value.getHours()).padStart(2, '0');
      const minutes: string = String(entry.value.getMinutes()).padStart(2, '0');
      return `${formatDateOnly(entry.value)}T${hours}:${minutes}`;
    }

    return formatDateOnly(entry.value);
  }

  orderDirectTicketsInCart(
    paymentMethod: PaymentMethod | undefined
  ): Observable<IApiResponse<IPurchase>> {
    return this.directTicketStore.placeOrder({
      tickets: this.products,
      paymentMethod,
      source: ETransactionSource.webShop,
      paymentProvider: this.configurationStore.getEnumConfiguration(
        CONFIGURATION_SETTING_KEYS.PaymentProvider,
        EPaymentProviders
      ),
      shouldSaveFellowTravelers:
        this.orderOptionsForm.value.shouldSaveFellowTravelers ?? false,
    });
  }

  orderDirectTicketsInCartAsGuest(
    customerId: string
  ): Observable<IApiResponse<IBuyResponse>> {
    return this.directTicketStore.placeGuestOrder({
      tickets: this.products,
      customerId,
      source: ETransactionSource.webShop,
      paymentProvider: this.configurationStore.getEnumConfiguration(
        CONFIGURATION_SETTING_KEYS.PaymentProvider,
        EPaymentProviders
      ),
    });
  }

  private setCurrentProductFormValuesAndValidators(
    product: TariffProductDefinition | undefined,
    selectionAttributes: TariffSelectionAttributeValue[],
    productSortOptions: TariffSelectionAttributeValue[],
    travelDate?: Date | null,
    amount?: number
  ): void {
    if (travelDate) {
      this.shoppingCartFormService.setCurrentProductFormValuesAndValidators(
        this.productForms.controls[0],
        product,
        selectionAttributes,
        productSortOptions,
        travelDate,
        amount
      );
    }
  }

  private updateProductAmount(
    product: IProductInCart,
    amount: number
  ): boolean {
    const productIndex: number = this.productsInCart().indexOf(product);

    if (productIndex < 0) {
      return false;
    }

    product.productForm.controls.amount.setValue(amount);
    this.adjustNumberOfTicketOwnerForms(productIndex, amount);
    product.productForm.updateValueAndValidity();

    return true;
  }
}
